import { messageId } from "@smartmanager/shell-ui/shared/i18n/messageId";
import { DEFAULT_REQUEST_LOCALE, type SupportedLocale } from "@smartmanager/shell-ui/shared/i18n/requestLocale";
import type { LocaleMessages } from "@smartmanager/shell-ui/shared/i18n/i18nContext";

// @scans src/shared/i18n/locales/*.json
const EMPTY: LocaleMessages = Object.freeze({});
const loaded = new Map<SupportedLocale, Promise<LocaleMessages>>();

/**
 * Loads one locale's translations. English is the source language and needs
 * none. Each locale file is its own chunk, so a browser only downloads the
 * language it switches to.
 */
export function loadLocaleMessages(locale: SupportedLocale): Promise<LocaleMessages> {
  if (locale === DEFAULT_REQUEST_LOCALE) return Promise.resolve(EMPTY);

  let messages = loaded.get(locale);
  if (!messages) {
    messages = import(`./locales/${locale}.json`).then(
      (module: { default: Record<string, unknown> }) => keyByMessageId(module.default),
    );
    // A failed load must not poison later attempts.
    messages.catch(() => loaded.delete(locale));
    loaded.set(locale, messages);
  }
  return messages;
}

/** A catalog (English source to translation) keyed the way the i18n context reads it. */
export function keyByMessageId(catalog: Record<string, unknown>): LocaleMessages {
  const messages: Record<string, string> = {};
  for (const [source, translation] of Object.entries(catalog)) {
    if (typeof translation === "string" && translation) messages[messageId(source)] = translation;
  }
  return messages;
}
